import React from 'react';
import { useTranslation } from 'react-i18next';
import { PriceDiscrepancy } from 'services/invoiceDiscrepancies/types';
import { RelatedTransactionsContent } from './RelatedTransactionsContent';
import { formatCurrencyOrEmptyValue } from 'support/helpers/currency/currency';
import { Divider } from 'components/Divider/Divider';
import { EMPTY_FIELD } from 'support/helpers/const/const';
import { roundNumber } from 'support/helpers/numbers/numbers';

type PriceDiscrepancyViewProps = Pick<PriceDiscrepancy, 'data' | 'relatedTransactions' | 'currency'>;

export const PriceDiscrepancyView: React.FC<PriceDiscrepancyViewProps> = ({ data, relatedTransactions, currency }) => {
  const { unit_price: unitPrice, consolidated_unit_price: consolidatedUnitPrice, charges, allowances } = data;
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-4">
      <div className="grid w-full grid-cols-3 gap-y-5 text-xs text-gray-500">
        <div className="contents">
          <span></span>
          <span className="text-end">
            {t('invoiceVerification:detailsPage.discrepanciesModal.content.priceDiscrepancy.headers.expected')}
          </span>
          <span className="text-end">
            {t('invoiceVerification:detailsPage.discrepanciesModal.content.priceDiscrepancy.headers.actual')}
          </span>
        </div>
        <div className="contents">
          <span>
            {t(
              'invoiceVerification:detailsPage.discrepanciesModal.content.priceDiscrepancy.rows.consolidatedUnitPrice',
            )}
          </span>
          <span className="text-end font-semibold text-gray-900">
            {formatCurrencyOrEmptyValue(consolidatedUnitPrice.expected, currency)}
          </span>
          <span className="text-end font-semibold text-gray-900">
            {formatCurrencyOrEmptyValue(consolidatedUnitPrice.actual, currency)}
          </span>
        </div>
        <div className="col-span-3">
          <Divider />
        </div>
        <div className="contents">
          <span>{t('invoiceVerification:detailsPage.discrepanciesModal.content.priceDiscrepancy.rows.unitPrice')}</span>
          <span className="text-end text-gray-900">{formatCurrencyOrEmptyValue(unitPrice.expected, currency)}</span>
          <span className="text-end text-gray-900">{formatCurrencyOrEmptyValue(unitPrice.actual, currency)}</span>
        </div>
        <div className="contents">
          <span>
            {t('invoiceVerification:detailsPage.discrepanciesModal.content.priceDiscrepancy.rows.allowances')}
          </span>
          <span className="text-end text-gray-900">
            {formatAmountWithPercentage(allowances?.expected, (value) => value * -1)}
          </span>
          <span className="text-end text-gray-900">
            {formatAmountWithPercentage(allowances?.actual, (value) => value * -1)}
          </span>
        </div>
        <div className="contents">
          <span>{t('invoiceVerification:detailsPage.discrepanciesModal.content.priceDiscrepancy.rows.charges')}</span>
          <span className="text-end text-gray-900">{formatAmountWithPercentage(charges.expected)}</span>
          <span className="text-end text-gray-900">{formatAmountWithPercentage(charges.actual)}</span>
        </div>
      </div>
      <Divider />
      <RelatedTransactionsContent relatedTransactions={relatedTransactions} />
    </div>
  );
};

const formatAmountWithPercentage = (
  value: { amount: number | null; percentage: number | null } | null | undefined,
  transformer: (value: number) => number = (value) => value,
): string => {
  if (!value || value.percentage === null) {
    return EMPTY_FIELD;
  }

  const percentage = value.percentage !== null ? `${roundNumber(transformer(value.percentage), 2)}%` : EMPTY_FIELD;

  if (percentage !== EMPTY_FIELD) {
    return percentage;
  }

  return EMPTY_FIELD;
};
